import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface UseFeedPagingParams {
  itemCount: number;
  initialIndex: number;
}

interface UseFeedPagingResult {
  scrollRef: React.RefObject<HTMLDivElement>;
  setSlideRef: (index: number) => (el: HTMLDivElement | null) => void;
  activeIndex: number;
  hasUserScrolled: boolean;
}

const ACTIVE_SLIDE_THRESHOLD = 0.6;

const useFeedPaging = ({
  itemCount,
  initialIndex,
}: UseFeedPagingParams): UseFeedPagingResult => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [hasUserScrolled, setHasUserScrolled] = useState(false);
  const didInitialScroll = useRef(false);

  const slideRefCallbacks = useRef(
    new Map<number, (el: HTMLDivElement | null) => void>()
  );

  const setSlideRef = useCallback((index: number) => {
    let callback = slideRefCallbacks.current.get(index);
    if (!callback) {
      callback = (el: HTMLDivElement | null) => {
        slideRefs.current[index] = el;
      };
      slideRefCallbacks.current.set(index, callback);
    }
    return callback;
  }, []);

  useLayoutEffect(() => {
    if (didInitialScroll.current || itemCount === 0) return;
    const target = slideRefs.current[initialIndex];
    if (target) {
      target.scrollIntoView();
      didInitialScroll.current = true;
      setActiveIndex(initialIndex);
    }
  }, [itemCount, initialIndex]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || itemCount === 0) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(
              (entry.target as HTMLElement).dataset.index ?? '0'
            );
            setActiveIndex(index);
          }
        });
      },
      { root, threshold: ACTIVE_SLIDE_THRESHOLD }
    );
    slideRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [itemCount]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || itemCount === 0) return undefined;
    const markUserScrolled = () => setHasUserScrolled(true);
    root.addEventListener('touchmove', markUserScrolled, { passive: true });
    root.addEventListener('wheel', markUserScrolled, { passive: true });
    return () => {
      root.removeEventListener('touchmove', markUserScrolled);
      root.removeEventListener('wheel', markUserScrolled);
    };
  }, [itemCount]);

  return { scrollRef, setSlideRef, activeIndex, hasUserScrolled };
};

export default useFeedPaging;
